import { useEffect, useMemo, useState, useRef } from "react";
import { Input, ConfigProvider, Select } from "antd";
import {
  BankOutlined,
  GlobalOutlined,
  MailOutlined,
  ShopOutlined,
  NotificationOutlined,
  FormOutlined,
  AimOutlined,
} from "@ant-design/icons";
import "./App.css";

const API_BASE_URL = "http://localhost:5173";

const FALLBACK_HEAR_ABOUT_OPTIONS = [
  { value: 1, label: "Friend or colleague referral" },
  { value: 2, label: "Social media" },
  { value: 3, label: "Google search" },
  { value: 4, label: "YouTube" },
  { value: 5, label: "Online community" },
  { value: 6, label: "Event or webinar" },
  { value: 18, label: "Other" },
];

// Real options from admira-frontend signup flow (SignupAddInfo.tsx).
const MAIN_GOAL_OPTIONS = [
  { value: 36, label: "Unified Data Dashboard" },
  { value: 37, label: "Real-Time Performance Insights" },
  { value: 38, label: "Comprehensive Multi-Touch Attribution" },
  { value: 39, label: "Privacy-Resilient Tracking" },
  { value: 40, label: "Data-Driven ROI Optimization" },
  { value: 41, label: "Customizable Reporting" },
  { value: 42, label: "Seamless Integrations" },
  { value: 43, label: "Actionable Analytics" },
  { value: 44, label: "Cross-Channel Visibility" },
  { value: 45, label: "Scalable Solutions" },
];

const FALLBACK_INDUSTRY_OPTIONS = [
  { value: 1, label: "Marketing and Advertising" },
  { value: 2, label: "E-commerce" },
  { value: 3, label: "SaaS" },
  { value: 4, label: "Education" },
  { value: 5, label: "Healthcare" },
  { value: 6, label: "Finance" },
  { value: 7, label: "Other" },
];

const PLAN_OPTIONS = [
  {
    id: "starter",
    title: "Starter",
    monthlyPrice: 199,
    annualMonthlyPrice: 149,
    features: ["Up to 10,000 MTU", "Basic analytics", "Up to 1 seat"],
  },
  {
    id: "growth",
    title: "Growth",
    monthlyPrice: 399,
    annualMonthlyPrice: 299,
    popular: true,
    features: ["Up to 30,000 MTU", "Basic analytics", "Up to 5 seats"],
  },
  {
    id: "enterprise",
    title: "Enterprise",
    price: null,
    features: [
      "Unlimited contacts",
      "Custom BI and reporting",
      "Dedicated CSM",
      "SLA and security review",
      "Unlimited team members",
    ],
  },
];

const CustomSelectIcon = () => (
  <svg width="12px" height="8px" viewBox="0 0 14 7" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13.0229 1.14797L7.39792 6.77297C7.34568 6.82527 7.28365 6.86676 7.21536 6.89507C7.14707 6.92337 7.07388 6.93794 6.99996 6.93794C6.92603 6.93794 6.85284 6.92337 6.78455 6.89507C6.71627 6.86676 6.65423 6.82527 6.60199 6.77297L0.976986 1.14797C0.89823 1.0693 0.844585 0.969033 0.822844 0.85986C0.801104 0.750688 0.812244 0.637519 0.854855 0.534681C0.897465 0.431842 0.969631 0.34396 1.06221 0.282157C1.1548 0.220355 1.26364 0.187413 1.37495 0.1875H12.625C12.7363 0.187413 12.8451 0.220355 12.9377 0.282157C13.0303 0.34396 13.1024 0.431842 13.1451 0.534681C13.1877 0.637519 13.1988 0.750688 13.1771 0.85986C13.1553 0.969033 13.1017 1.0693 13.0229 1.14797Z" fill="rgba(0,0,0,0.7)"></path>
  </svg>
);

function App() {
  const apiBaseUrl = useMemo(() => API_BASE_URL.replace(/\/+$/, ""), []);

  // OBSERVER FOR IFRAME
  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      // Tomamos el elemento observado
      const entry = entries[0];

      // Usamos getBoundingClientRect, que es la altura física exacta del elemento
      const newHeight = entry.target.getBoundingClientRect().height;

      window.parent.postMessage(
        { type: "IFRAME_RESIZE", height: newHeight },
        "*",
      );
    });

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  // END OF OBSERVER FOR IFRAME

  const [hearAboutOptions, setHearAboutOptions] = useState(
    FALLBACK_HEAR_ABOUT_OPTIONS,
  );
  const [industryOptions, setIndustryOptions] = useState(
    FALLBACK_INDUSTRY_OPTIONS,
  );
  const [formData, setFormData] = useState({
    companyName: "",
    companyDomain: "",
    industry: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    howDidYouHear: "",
    hearAboutOther: "",
    mainGoal: "",
    selectedPlan: "growth",
    billingCycle: "monthly",
    enterpriseBillingCycle: "monthly",
    enterpriseMonthlyPrice: "",
    enterpriseMtuLimit: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const isAnnualBilling = formData.billingCycle === "annual";

  useEffect(() => {
    const fetchParametrics = async () => {
      try {
        const [hearAboutResponse, industryResponse] = await Promise.all([
          fetch(`${apiBaseUrl}/api/parametrics/hear_about`, {
            method: "GET",
            credentials: "include",
          }),
          fetch(`${apiBaseUrl}/api/parametrics/industry`, {
            method: "GET",
            credentials: "include",
          }),
        ]);

        if (hearAboutResponse.ok) {
          const hearAboutData = await hearAboutResponse.json();
          if (Array.isArray(hearAboutData) && hearAboutData.length > 0) {
            setHearAboutOptions(
              hearAboutData.map((option) => ({
                value: option.id,
                label: option.value,
              })),
            );
          }
        }

        if (!industryResponse.ok) {
          return;
        }

        const industryData = await industryResponse.json();
        if (Array.isArray(industryData) && industryData.length > 0) {
          setIndustryOptions(
            industryData.map((option) => ({
              value: option.id,
              label: option.value,
            })),
          );
        }
      } catch (fetchError) {
        // Keep fallback options when backend parametrics cannot be reached/authenticated.
      }
    };

    fetchParametrics();
  }, [apiBaseUrl]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSelectChange = (value, name) => {
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleBillingCycleChange = (billingCycle) => {
    setFormData((previous) => ({
      ...previous,
      billingCycle,
    }));
  };

  const handleEnterpriseBillingCycleChange = (enterpriseBillingCycle) => {
    setFormData((previous) => ({
      ...previous,
      enterpriseBillingCycle,
    }));
  };

  const getDisplayedPlanPrice = (plan) => {
    if (plan.id === "enterprise") {
      return null;
    }

    return isAnnualBilling ? plan.annualMonthlyPrice : plan.monthlyPrice;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    setError("");
    setIsSubmitting(true);

    try {
      const csrfResponse = await fetch(`${apiBaseUrl}/api/csrf-token`, {
        method: "GET",
        credentials: "include",
      });

      if (!csrfResponse.ok) {
        throw new Error("Could not fetch CSRF token from backend.");
      }

      const csrfPayload = await csrfResponse.json();
      const csrfToken = csrfPayload.csrf_token || "";

      const normalizedEmail = formData.email.trim();

      const createUserPayload = {
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        email_address: normalizedEmail,
        password: formData.password,
        how_did_you_hear_about_us: Number(formData.howDidYouHear),
        main_goal_with_admira: Number(formData.mainGoal),
        selected_plan: formData.selectedPlan,
      };

      const createUserResponse = await fetch(`${apiBaseUrl}/api/create-user`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify(createUserPayload),
      });

      const userContentType =
        createUserResponse.headers.get("content-type") || "";
      const createUserData = userContentType.includes("application/json")
        ? await createUserResponse.json()
        : await createUserResponse.text();

      if (!createUserResponse.ok) {
        const backendError =
          typeof createUserData === "string"
            ? createUserData
            : createUserData?.error || "Could not create user.";
        throw new Error(backendError);
      }

      const companyPayload = {
        name: formData.companyName.trim(),
        email: normalizedEmail,
        domain: formData.companyDomain.trim(),
        industry: Number(formData.industry),
        main_goal: Number(formData.mainGoal),
        hear_about: Number(formData.howDidYouHear),
      };

      if (
        Number(formData.howDidYouHear) === 18 &&
        formData.hearAboutOther.trim()
      ) {
        companyPayload.hear_about_other = formData.hearAboutOther.trim();
      }

      const createCompanyWorkspaceResponse = await fetch(
        `${apiBaseUrl}/api/create-company-workspace`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify(companyPayload),
        },
      );

      const companyContentType =
        createCompanyWorkspaceResponse.headers.get("content-type") || "";
      const createCompanyData = companyContentType.includes("application/json")
        ? await createCompanyWorkspaceResponse.json()
        : await createCompanyWorkspaceResponse.text();

      if (!createCompanyWorkspaceResponse.ok) {
        const backendError =
          typeof createCompanyData === "string"
            ? createCompanyData
            : createCompanyData?.error || "Could not create company workspace.";
        throw new Error(backendError);
      }

      const enterpriseMonthlyPriceCents =
        formData.selectedPlan === "enterprise"
          ? Math.round(Number(formData.enterpriseMonthlyPrice || "0") * 100)
          : 0;

      if (
        formData.selectedPlan === "enterprise" &&
        enterpriseMonthlyPriceCents <= 0
      ) {
        throw new Error("Please add a valid enterprise monthly price.");
      }

      const checkoutResponse = await fetch(
        `${apiBaseUrl}/api/create-checkout-session`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: normalizedEmail,
            first_name: formData.firstName.trim(),
            last_name: formData.lastName.trim(),
            plan: formData.selectedPlan,
            billing_cycle:
              formData.selectedPlan === "enterprise"
                ? formData.enterpriseBillingCycle
                : formData.billingCycle,
            enterprise_monthly_price_cents: enterpriseMonthlyPriceCents,
            mtu_limit: formData.selectedPlan === "enterprise" ? Number(formData.enterpriseMtuLimit) : undefined,
            success_url: `https://app.getadmira.com/login?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: `${window.location.origin}/?checkout=cancel`,
          }),
        },
      );

      const checkoutContentType =
        checkoutResponse.headers.get("content-type") || "";
      const checkoutData = checkoutContentType.includes("application/json")
        ? await checkoutResponse.json()
        : { error: await checkoutResponse.text() };

      if (!checkoutResponse.ok) {
        throw new Error(
          checkoutData?.error || "Could not create Stripe checkout session.",
        );
      }

      if (!checkoutData?.url) {
        throw new Error("Stripe checkout URL is missing in backend response.");
      }

      const createdUserId =
        typeof createUserData === "object" && createUserData
          ? createUserData.id
          : null;
      setMessage(
        createdUserId
          ? `User and workspace created (user id: ${createdUserId}). Redirecting to Stripe checkout...`
          : "User and workspace created. Redirecting to Stripe checkout...",
      );
      // Enviamos un mensaje a Webflow con la URL de Stripe
      window.parent.postMessage(
        {
          type: "REDIRECT_TO_CHECKOUT",
          url: checkoutData.url,
        },
        "*",
      );
    } catch (submitError) {
      setError(submitError.message || "Unexpected error while creating user.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          // Esto cambia el color principal a un verde esmeralda
          colorPrimary: "#124aff",
        },
        components: {
          Input: {
            colorBgContainer: "#f5f6f8",
          },
          Select: {
            colorBgContainer: "#f5f6f8",
          },
        },
      }}
    >
      {/* ref={containerRef} is for iframe functions */}
      <main className="App" style={{ overflow: "hidden" }}>
        <section className="form-card" ref={containerRef}>
          <form onSubmit={handleSubmit} className="onboarding-form">
            <div className="cols">
              <div className="col">
                <p className="plans-title">Get started</p>
                <div className="inputs-cols">
                  <div className="input-wrapper">
                    <label htmlFor="firstName">First name</label>
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="John"
                      required
                      prefix={
                        <img src="https://admira-public-bucket.s3.us-east-2.amazonaws.com/img/face.svg" alt="" />
                      }
                    />
                  </div>

                  <div className="input-wrapper">
                    <label htmlFor="lastName">Last name</label>
                    <Input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Doe"
                      required
                      prefix={
                        <img src="https://admira-public-bucket.s3.us-east-2.amazonaws.com/img/face.svg" alt="" />
                      }
                    />
                  </div>

                </div>
                <div className="input-wrapper">
                  <label htmlFor="email">Email</label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="name@company.com"
                    required
                    prefix={<MailOutlined />}
                  />
                </div>
                <div className="input-wrapper">
                  <label htmlFor="password">Password</label>
                  <Input.Password
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create your password"
                    minLength={3}
                    autoComplete="new-password"
                    required
                  />
                </div>
                <div className="inputs-cols">
                  <div className="input-wrapper">
                    <label htmlFor="companyName">Company name</label>
                    <Input
                      id="companyName"
                      name="companyName"
                      type="text"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="My Company Corp"
                      required
                      prefix={<BankOutlined />}
                    />
                  </div>
                  <div className="input-wrapper">
                    <label htmlFor="companyDomain">Company domain</label>
                    <Input
                      id="companyDomain"
                      name="companyDomain"
                      type="text"
                      value={formData.companyDomain}
                      onChange={handleChange}
                      placeholder="company.com"
                      required
                      prefix={<GlobalOutlined />}
                    />
                  </div>
                </div>

                <div className="input-wrapper">
                  <label htmlFor="industry">Industry</label>
                  <Select
                    id="industry"
                    name="industry"
                    value={formData.industry}
                    onChange={(value) => handleSelectChange(value, "industry")}
                    required
                    className="select-ant"
                    suffixIcon={<CustomSelectIcon />}
                    prefix={<ShopOutlined />}
                  >
                    <option value="">Select an option</option>
                    {industryOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="input-wrapper">
                  <label htmlFor="howDidYouHear">
                    How did you hear about us
                  </label>
                  <Select
                    id="howDidYouHear"
                    name="howDidYouHear"
                    value={formData.howDidYouHear}
                    onChange={(value) => handleSelectChange(value, "howDidYouHear")}
                    required
                    className="select-ant"
                    suffixIcon={<CustomSelectIcon />}
                    prefix={<NotificationOutlined />}
                  >
                    <option value="">Select an option</option>
                    {hearAboutOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>

                {Number(formData.howDidYouHear) === 18 ? (
                  <div className="input-wrapper">
                    <label htmlFor="hearAboutOther">
                      How did you hear about us (other)
                    </label>
                    <Input
                      id="hearAboutOther"
                      name="hearAboutOther"
                      type="text"
                      value={formData.hearAboutOther}
                      onChange={handleChange}
                      placeholder="Please specify..."
                      required
                      prefix={<FormOutlined />}
                    />
                  </div>
                ) : null}

                <div className="input-wrapper">
                  <label htmlFor="mainGoal">
                    What is your main goal with Admira
                  </label>
                  <Select
                    id="mainGoal"
                    name="mainGoal"
                    value={formData.mainGoal}
                    onChange={(value) => handleSelectChange(value, "mainGoal")}
                    required
                    className="select-ant"
                    suffixIcon={<CustomSelectIcon />}
                    prefix={<AimOutlined />}
                  >
                    <option value="">Select an option</option>
                    {MAIN_GOAL_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="col">
                <div className="plans-wrapper">
                  <div className="plans-header">
                    <p className="plans-title">Choose your plan</p>
                    <div className="billing-toggle-wrap">
                      <div
                        className="billing-toggle"
                        role="radiogroup"
                        aria-label="Billing cycle"
                      >
                        <button
                          type="button"
                          className={`billing-toggle-option ${formData.billingCycle === "monthly"
                            ? "billing-toggle-option-active"
                            : ""
                            }`}
                          onClick={() => handleBillingCycleChange("monthly")}
                          aria-pressed={formData.billingCycle === "monthly"}
                        >
                          Monthly
                        </button>
                        <button
                          type="button"
                          className={`billing-toggle-option ${formData.billingCycle === "annual"
                            ? "billing-toggle-option-active"
                            : ""
                            }`}
                          onClick={() => handleBillingCycleChange("annual")}
                          aria-pressed={formData.billingCycle === "annual"}
                        >
                          Annual{" "}
                          <span className="billing-save-badge">Save 20%</span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="plans-grid">
                    {PLAN_OPTIONS.map((plan) => {
                      const isSelected = formData.selectedPlan === plan.id;
                      const displayedPrice = getDisplayedPlanPrice(plan);
                      return (
                        <article
                          key={plan.id}
                          className={`plan-card ${isSelected ? "plan-card-selected" : ""}`}
                        >
                          {plan.popular ? (
                            <span className="popular-badge">Popular</span>
                          ) : null}
                          <h3>{plan.title}</h3>
                          <div className="plan-price">
                            {displayedPrice ? (
                              <>
                                {isAnnualBilling ? (
                                  <small className="plan-price-original">
                                    ${plan.monthlyPrice}/month
                                  </small>
                                ) : null}
                                <p>
                                  ${displayedPrice} <small>USD</small>
                                </p>
                                <small>/month</small>
                              </>
                            ) : (
                              <p>Custom</p>
                            )}
                          </div>
                          {plan.id === "enterprise" && isSelected ? (
                            <div className="enterprise-inline-pricing">
                              <div
                                className="enterprise-inline-cycle"
                                role="radiogroup"
                                aria-label="Enterprise billing cycle"
                              >
                                <button
                                  type="button"
                                  className={`enterprise-inline-cycle-option ${formData.enterpriseBillingCycle ===
                                    "monthly"
                                    ? "enterprise-inline-cycle-option-active"
                                    : ""
                                    }`}
                                  onClick={() =>
                                    handleEnterpriseBillingCycleChange(
                                      "monthly",
                                    )
                                  }
                                  aria-pressed={
                                    formData.enterpriseBillingCycle ===
                                    "monthly"
                                  }
                                >
                                  Monthly
                                </button>
                                <button
                                  type="button"
                                  className={`enterprise-inline-cycle-option ${formData.enterpriseBillingCycle === "annual"
                                    ? "enterprise-inline-cycle-option-active"
                                    : ""
                                    }`}
                                  onClick={() =>
                                    handleEnterpriseBillingCycleChange("annual")
                                  }
                                  aria-pressed={
                                    formData.enterpriseBillingCycle === "annual"
                                  }
                                >
                                  Annual
                                </button>
                              </div>
                              <div className="enterprise-inline-input-wrap">
                                <span>$</span>
                                <Input
                                  id="enterpriseMonthlyPrice"
                                  name="enterpriseMonthlyPrice"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={formData.enterpriseMonthlyPrice}
                                  onChange={handleChange}
                                  placeholder="499"
                                  required={isSelected}
                                />
                                <small>
                                  {formData.enterpriseBillingCycle === "annual"
                                    ? "USD / year"
                                    : "USD / month"}
                                </small>
                              </div>
                              <div className="enterprise-inline-input-wrap">
                                <Input
                                  id="enterpriseMtuLimit"
                                  name="enterpriseMtuLimit"
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={formData.enterpriseMtuLimit}
                                  onChange={handleChange}
                                  placeholder="100000"
                                  required={isSelected}
                                />
                                <small>MTU limit</small>
                              </div>
                            </div>
                          ) : null}
                          <ul>
                            {plan.features.map((feature) => (
                              <li key={feature}>{feature}</li>
                            ))}
                          </ul>
                          <button
                            type="button"
                            className="plan-button"
                            onClick={() =>
                              setFormData((previous) => ({
                                ...previous,
                                selectedPlan: plan.id,
                              }))
                            }
                          >
                            {isSelected ? "Selected" : `Choose ${plan.title}`}
                          </button>
                        </article>
                      );
                    })}
                  </div>
                </div>
                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting
                    ? "Creating user and workspace..."
                    : "Create user"}
                </button>
              </div>
            </div>
          </form>

          {message ? <p className="status success">{message}</p> : null}
          {error ? <p className="status error">{error}</p> : null}
        </section>
      </main>
    </ConfigProvider>
  );
}

export default App;
